import { gql } from 'apollo-boost';

import { minusPhraseLibraryConstantsFragment } from './minus-phrase';

export const vcardConstantsFragment = gql`
    fragment vcardConstantsFragment on GdDirectConstantsVcardValidationData {
        companyNameMaxLength
        contactPersonMaxLength
        contactEmailMaxLength
        extraMessageMaxLength
        countryMaxLength
        cityMaxLength
        streetMaxLength
        houseWithBuildingMaxLength
        apartmentMaxLength

        instantMessengerLoginMaxLength

        pointOnMapLongitudeMin
        pointOnMapLongitudeMax
        pointOnMapLatitudeMin
        pointOnMapLatitudeMax

        countryCodeMaxLength
        cityCodeMaxLength
        phoneNumberMinLength
        phoneNumberMaxLength
        extensionMaxLength
        entirePhoneMinLength
        entirePhoneMaxLength
        entirePhoneWithExtensionMaxLength
    }
`;

export const vCardCampaignDataFragment = gql`
    fragment VCardCampaignDataFragment on GdCampaignVcard {
        id
        campaignId
        companyName
        contactPerson
        extraMessage
        email
        ogrn

        instantMessenger {
            type
            login
        }

        workTimes {
            daysOfWeek
            startTime {
                hour
                minute
            }
            endTime {
                hour
                minute
            }
        }

        phone {
            countryCode
            cityCode
            phoneNumber
            extension
        }

        address {
            geoId
            country
            city
            street
            houseWithBuilding
            apartment
            metroStationId
            metroStationName
            pointPrecision
            pointOnMap {
                x
                y
                x1
                y1
                x2
                y2
            }
        }
    }
`;

export const constantsCurrencyFragment = gql`
    fragment ConstantsCurrencyQueryFragment on GdCurrencyDescription {
        code
        minDailyBudget
        maxDailyBudget
        bigRate
        minPrice
        maxPrice
        bigRate
        defaultPrice
        minCpmPrice
        maxCpmPrice
        autobudgetAvgCpaWarning
        autobudgetAvgPriceWarning
        autobudgetClicksBundleWarning
        autobudgetMaxPriceWarning
        autobudgetSumWarning
        maxAutobudget
        maxAutobudgetBid
        maxAutobudgetClicksBundle
        minAutobudget
        minAutobudgetBid
        minAutobudgetClicksBundle
        minAutobudgetAvgCpa
        minAutobudgetAvgPrice
        brandSurveyBudgetThreshold
        autobudgetPayForConversionAvgCpaWarning
    }
`;

export const constantsFragment = gql`
    fragment ConstantsQueryFragment on GdDirectConstants {
        agencyOfflineReportMaximumDate
        strategyConstants {
            maxDailyBudgetChangesPerDay
        }
        currencyConstants {
            ...ConstantsCurrencyQueryFragment
        }
        alertData {
            enabled
            textRu
            textEn
        }
        validation {
            libMinusKeywordsConstants {
                ...minusPhraseLibraryConstantsFragment
            }
            adGroupConstants {
                maxAdGroupsCountPerUpdate
            }
            campaignConstants {
                minWarningBalanceInPercent
                maxWarningBalanceInPercent
            }
            vcardConstants {
                ...vcardConstantsFragment
            }
        }
        defaultValues {
            campaign {
                defaultWarningBalance
                defaultCheckPositionInterval
                defaultSmsTimeInterval {
                    endTime {
                        hour
                        minute
                    }
                    startTime {
                        hour
                        minute
                    }
                }
            }
        }
        configuration {
            balanceDomain
            canvasUiDomain
            spravIFrameUrl
            surveysUiUrl
        }
    }
    ${constantsCurrencyFragment}
    ${vcardConstantsFragment}
    ${minusPhraseLibraryConstantsFragment}
`;

export const campaignsContextQuery = gql`
    fragment CampaignContextFragment on GdClientAccess {
        canCreateCampaignTypes
        canCreateCampaign
        canViewInternalAds
        canEditInternalAds
        canManageLibMinusWords
        canExportToExcel
        canMassEditAdGroupRegions
    }
`;
export const footerQueryUserInfo = gql`
    fragment FooterQueryUserInfoFragment on GdUserInfo {
        name
        login
        email
        phone
    }
`;

export const operatorFeaturesFragment = gql`
    fragment OperatorFeaturesFragment on GdOperatorFeatures {
        isAdGroupUpdateAllowed
        isBannerUpdateAllowed
        isCpmBannerUpdateAllowed
        isBannerAimingAllowed
        isBannerAimingCpmAllowed
        isPerformanceAdEditAllowed
        isOutdoorGroupsEditAllowed
        isIndoorGroupsEditAllowed
        isRetargetingGridEnabled
        isAdsMassEditAllowed
        isCampaignAddAllowed
        isCampaignUpdateAllowed
        isCpmAdGroupUpdateAllowed
        isVideoGroupsEditAllowed
        isGeoproductGroupsEditAllowed
        isAudioGroupsEditAllowed
        isAggregatedStatusAllowed
        isMcBannerDnaEnabled
        isTargetTagsAllowed
        isOldPhraseSelectorWizardDisabled
    }
`;

export const footerQueryOfficeInfo = gql`
    fragment FooterQueryOfficeInfoFragment on GdOfficeContactData {
        description
        cityName
        phone
        phoneExtension
    }
`;

export const footerQueryOperator = gql`
    fragment FooterQueryOperatorFragment on GdOperatorInfo {
        tuneSecretKey
        workCurrency
        isSelfAgency
        isAgency
        info {
            login
        }
        access {
            canEditInternationalization
            canSearchPhrasesDuplicates
            canEditAdDomain
            canViewListOfInternalAdManagers
            canViewListOfInternalAdProducts
            canEditInternalAdManagers
            canEditInternalAdProducts
            actions
        }
        officeInfo {
            cityContact {
                ...FooterQueryOfficeInfoFragment
            }
            regionContact {
                ...FooterQueryOfficeInfoFragment
            }
            regionContact2 {
                ...FooterQueryOfficeInfoFragment
            }
        }
        managerInfo {
            ...FooterQueryUserInfoFragment
        }
        chiefInfo {
            ...FooterQueryUserInfoFragment
        }
        agencyInfo {
            ...FooterQueryUserInfoFragment
        }
    }
    ${footerQueryOfficeInfo}
    ${footerQueryUserInfo}
`;

export const footerQueryAgencyInfo = gql`
    fragment FooterQueryAgencyInfoFragment on GdAgencyInfo {
        clientId
        userId
        name
        login
        email
        phone
        representativeName
    }
`;

export const footerQueryClient = gql`
    fragment FooterQueryClientFragment on GdClient {
        info {
            id
            workCurrency
            countryRegionId
            translocalGeotreeType
            autoVideo
            internalAdProductName
            managersInfo {
                ...FooterQueryUserInfoFragment
            }
            agencyInfo {
                ...FooterQueryAgencyInfoFragment
            }
        }
        access {
            canUseVideoConstructor
            canPayByCash
            canManageFavoriteCampaigns
            canCopyCampaign
            canManageUsingCommander
            canMonitorPhrasesPositions
            canTransferMoney
            operatorIsClient
            canUploadDocuments
            canManageUsingXLS
            canManageRetargertingConditions
            canManageTurboLandings
            canSeeClientRepresentatives
            canManageVCards
            canViewOfflineReports
            canManageUsingApi
            canCreateCampaign
            canCreateCampaignTypes
            operatorIsClientChief
            canManageCreatives
            canManageFeeds
            canCheckCampaignsStats
            canViewUserLogs
            canViewMobileApps
            canSeeDealsLinkEverywhere
            canSeeAgencyClients
        }
    }
    ${footerQueryUserInfo}
    ${footerQueryAgencyInfo}
`;

export const walletsData = gql`
    fragment WalletsDataFragment on GdWallet {
        id
        status {
            enabled
        }
        budget {
            sum
            showMode
            type
        }
        sum
        currency
        actions
    }
`;

export const commonQuery = gql`
    query CommonQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            measurerAccounts
            wallets {
                ...WalletsDataFragment
            }
            access {
                ...CampaignContextFragment
            }
            features {
                hasAbSegments
                isAloneTrafaretOptionEnabled
                isBidCorrectionSearchEnabled
                isCampaignDefaultOrganizationsEnabled
                isContentPromotionCampaignInDnaSupported
                isContentPromotionVideoInGridSupported
                isContentPromotionCollectionInGridSupported
                isCpcDeviceModifiersAllowed
                isCpmBannerCampaignAddAllowed
                isCpmBannerCampaignUpdateAllowed
                isCpmCampaignsEnabled
                isCpmIndoorSegmentsEnabled
                isCpmVideoSeveralPixelsEnabled
                isDefaultAutobudgetAvgCpaEnabled
                isEmptyRetCondInCpmGroupEnabled
                isHonestAddTextAdGroupAllowed
                isGoalsStatEnabled
                isGridEnabled
                isInAppEventsInRmpEnabled
                isInternalAdsAllowed
                isMassEditRegionsEnabled
                isNewKeywordsEditorForDnaEnabled
                isOverlayCreativesEnabled
                isTargetTagsAllowed
                isTextBannerInterestsRetCondEnabled
                isTextCampaign8SitelinksEnabled
                isTycoonOrganizationsEnabled
                isRoiMeaningfulGoalsOptimizationEnabled
                isVideoConstructorEnabled
                isZeroSpeedPageForGeoproductEnabled
                relevanceMatchForNewGroupsEnabled

                isAdmetricaMeasurerCpmBannerAllowed
                isAdmetricaMeasurerCpmDealsAllowed
                isAdmetricaMeasurerCpmAudioAllowed
                isAdmetricaMeasurerCpmGeoproductAllowed
                isAdmetricaMeasurerCpmVideoAllowed
                isAdmetricaMeasurerCpmYndxFrontpageAllowed

                isAdlooxMeasurerCpmBannerAllowed
                isAdlooxMeasurerCpmAudioAllowed
                isAdlooxMeasurerCpmDealsAllowed
                isAdlooxMeasurerCpmGeoproductAllowed
                isAdlooxMeasurerCpmVideoAllowed
                isAdlooxMeasurerCpmYndxFrontpageAllowed

                isAdriverMeasurerCpmBannerAllowed
                isAdriverMeasurerCpmAudioAllowed
                isAdriverMeasurerCpmDealsAllowed
                isAdriverMeasurerCpmGeoproductAllowed
                isAdriverMeasurerCpmVideoAllowed
                isAdriverMeasurerCpmYndxFrontpageAllowed

                isCpaPayForConversionsStrategyAllowed

                isDvMeasurerCpmBannerAllowed
                isDvMeasurerCpmAudioAllowed
                isDvMeasurerCpmDealsAllowed
                isDvMeasurerCpmGeoproductAllowed
                isDvMeasurerCpmVideoAllowed
                isDvMeasurerCpmYndxFrontpageAllowed

                isIntegralAdScienceMeasurerCpmBannerAllowed
                isIntegralAdScienceMeasurerCpmAudioAllowed
                isIntegralAdScienceMeasurerCpmDealsAllowed
                isIntegralAdScienceMeasurerCpmGeoproductAllowed
                isIntegralAdScienceMeasurerCpmVideoAllowed
                isIntegralAdScienceMeasurerCpmYndxFrontpageAllowed

                isLandingsWizardAllowed

                isMediascopeMeasurerCpmBannerAllowed
                isMediascopeMeasurerCpmAudioAllowed
                isMediascopeMeasurerCpmDealsAllowed
                isMediascopeMeasurerCpmGeoproductAllowed
                isMediascopeMeasurerCpmVideoAllowed
                isMediascopeMeasurerCpmYndxFrontpageAllowed

                isMoatMeasurerCpmBannerAllowed
                isMoatMeasurerCpmAudioAllowed
                isMoatMeasurerCpmDealsAllowed
                isMoatMeasurerCpmGeoproductAllowed
                isMoatMeasurerCpmVideoAllowed
                isMoatMeasurerCpmYndxFrontpageAllowed

                isMediascopeMeasurerEnabled

                isSizmekMeasurerCpmBannerAllowed
                isSizmekMeasurerCpmAudioAllowed
                isSizmekMeasurerCpmDealsAllowed
                isSizmekMeasurerCpmGeoproductAllowed
                isSizmekMeasurerCpmVideoAllowed
                isSizmekMeasurerCpmYndxFrontpageAllowed

                isWeboramaMeasurerCpmBannerAllowed
                isWeboramaMeasurerCpmAudioAllowed
                isWeboramaMeasurerCpmDealsAllowed
                isWeboramaMeasurerCpmGeoproductAllowed
                isWeboramaMeasurerCpmVideoAllowed
                isWeboramaMeasurerCpmYndxFrontpageAllowed

                isTnsEnabled
                isContentPromotionVideoInGridSupported
                isContentPromotionCollectionInGridSupported
            }
            ...FooterQueryClientFragment
        }
        constants {
            ...ConstantsQueryFragment
        }
        operator {
            ...FooterQueryOperatorFragment
            features {
                ...OperatorFeaturesFragment
            }
        }
    }
    ${footerQueryClient}
    ${footerQueryOperator}
    ${constantsFragment}
    ${campaignsContextQuery}
    ${walletsData}
    ${operatorFeaturesFragment}
`;

export const findAndReplaceKeywordsPreview = gql`
    query FindAndReplaceKeywordsPreview($input: GdFindAndReplaceKeywordsInput!) {
        reqId: getReqId
        getFindAndReplaceKeywordsPreview(input: $input) {
            cacheKey
            totalCount
            rowset {
                minusKeywords
                changedMinusKeywords
                keyword
                changedKeyword
            }
            validationResult {
                errors {
                    path
                    code
                    params
                }
            }
        }
    }
`;

export const commonWallet = gql`
    query CommonWalletQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            wallets {
                sum
                currency
                status {
                    enabled
                }
            }
        }
    }
`;

export const balanceCartDataQuery = gql`
    query BalanceCartDataQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            wallets {
                id
            }
            features {
                isB2bBalanceCartEnabled
            }
        }
    }
`;

export const dealsCount = gql`
    query DealsCount($login: String!, $dealsFilter: GdiDealFilterInput) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            deals(filter: $dealsFilter) {
                totalCount
            }
        }
    }
`;

export const headerClientInfo = gql`
    query HeaderClientInfoQuery($login: String!) {
        client(searchBy: { login: $login }) {
            info {
                id
                chiefUserId
                agencyClientId
            }
            wallets {
                ...WalletsDataFragment
            }
        }
    }
    ${walletsData}
`;

export const headerMenu = gql`
    query MenuHeaderQuery {
        menuHeader {
            allowedMenuItems
        }
    }
`;

export const headerInfo = gql`
    query HeaderInfoQuery($login: String!, $dealsFilter: GdiDealFilterInput) {
        getReqId
        client(searchBy: { login: $login }) {
            info {
                id
                chiefUserId
                agencyClientId
            }
            deals(filter: $dealsFilter) {
                totalCount
            }
            features {
                isB2bBalanceCartEnabled
            }
            access {
                canSeeDealsLinkEverywhere
            }
            wallets {
                ...WalletsDataFragment
            }
        }
        operator {
            info {
                userId
            }
            isAgency
        }
    }
    ${walletsData}
`;

export const walletInfo = gql`
    query WalletInfoQuery($login: String!) {
        getReqId
        client(searchBy: { login: $login }) {
            wallets {
                ...WalletsDataFragment
            }
        }
    }
    ${walletsData}
`;

export const checkClientMutationState = gql`
    query CheckClientMutationStateQuery($input: GdCheckClientMutationStateInput!) {
        checkClientMutationState(input: $input) {
            state
        }
    }
`;

export const authorizationQuery = gql`
    query AuthorizationQuery {
        operator {
            chiefInfo {
                login
                clientId
                userId
            }
            isFreelancer
        }
    }
`;

export const clientMetrikaCountersQuery = gql`
    query ClientMetrikaCountersQuery($login: String!) {
        reqId: getReqId
        client(searchBy: { login: $login }) {
            metrikaCounters {
                counters
                isMetrikaAvailable
            }
        }
    }
`;

export const timeZoneGroupsQuery = gql`
    query TimeZoneGroupsQuery {
        reqId: getReqId
        constants {
            timezoneGroups {
                groupNick
                timezones {
                    id
                    name
                }
            }
        }
    }
`;

export const getPhoneCodeByRegionIdQuery = gql`
    query GetPhoneCodeByRegionIdQuery($input: GdRegionInput!) {
        getPhoneCodeByRegionId(region: $input) {
            codes
        }
    }
`;
